import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from "@/assets/logo.png";
import { navLinks } from '@/constants/navigation';
import { useScroll } from '@/hooks/useScroll';
import { Button } from '@/components/ui';

/**
 * Navbar - Main navigation component with mobile support and scroll behavior.
 */
const Navbar = () => {
  const isScrolled = useScroll(60);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  return (
    <>
      <a href="#content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg whitespace-nowrap">
        Skip to content
      </a>

      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isTransparent
            ? 'bg-transparent'
            : 'bg-dark/80 backdrop-blur-2xl border-b border-white/5 shadow-2xl shadow-black/20'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <img src={logo} alt="NFskills Logo" className="h-9 w-9 object-contain" />
              </div>
              <span className="text-xl font-heading font-black tracking-tight text-white transition-colors duration-300">
                NF<span className="text-primary">skills</span>
              </span>
            </Link>

            {/* Desktop links - staggered reveal on load */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.1 + i * 0.05,
                    ease: [0.16, 1, 0.3, 1] as const,
                  }}
                >
                  <Link
                    to={link.path}
                    className={`relative px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap ${
                      location.pathname === link.path
                        ? 'text-primary'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.label}
                    {location.pathname === link.path && (
                      <motion.div
                        layoutId="nav-indicator"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full"
                      />
                    )}
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="https://mcq.nfskills.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-bold text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200 whitespace-nowrap"
              >
                MCQ Portal
              </a>
              <a
                href="https://portal.nfskills.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-bold text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200 whitespace-nowrap"
              >
                Student Portal
              </a>
              <Button
                to="/courses"
                variant="primary"
                icon="ri-arrow-right-line"
                className="h-11 px-6 text-xs shadow-glow-hover"
              >
                Enroll Now
              </Button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="w-5 flex flex-col gap-[5px]">
                <span className={`block h-0.5 w-5 rounded-full bg-white transition-all duration-300 origin-center ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
                <span className={`block h-0.5 rounded-full bg-white transition-all duration-300 ${isMobileMenuOpen ? 'w-0 opacity-0' : 'w-3.5 opacity-100'}`}></span>
                <span className={`block h-0.5 w-5 rounded-full bg-white transition-all duration-300 origin-center ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-dark/95 backdrop-blur-2xl border-t border-white/10 overflow-hidden"
            >
              <div className="px-4 py-6 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'bg-primary/10 text-primary'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 pb-2 flex flex-col gap-3">
                  <a
                    href="https://mcq.nfskills.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-6 py-3 border border-white/15 text-white text-sm font-semibold rounded-xl hover:bg-white/5 transition-colors text-center"
                  >
                    MCQ Portal
                  </a>
                  <a
                    href="https://portal.nfskills.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-6 py-3 border border-white/15 text-white text-sm font-semibold rounded-xl hover:bg-white/5 transition-colors text-center"
                  >
                    Student Portal
                  </a>
                  <Button
                    to="/courses"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full h-13 justify-center"
                  >
                    Enroll Now
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;